import { msRemaining } from '@floorpoll/shared';
import { useEffect, useRef, useState } from 'react';

/** Ticks fast enough that the displayed second is never visibly stale. */
const TICK_MS = 250;

export interface Countdown {
  /** Whole seconds left, rounded up so the last visible number is 1, not 0. */
  secondsLeft: number;
  /** True once the clock has run out. The taker's buttons lock on this. */
  expired: boolean;
  /** Formatted as m:ss, or bare seconds while under a minute. */
  label: string;
}

/**
 * Story #77 — the countdown, measured against the server's clock rather than
 * this device's.
 *
 * A phone with a wrong clock is common enough in a room to matter, and the
 * deadline is an absolute instant, so a naive `Date.now()` comparison would
 * expire early or late by however far the device has drifted. Every payload
 * carrying a deadline carries the server's own time beside it, and the
 * difference between the two **at the moment the payload arrived** is this
 * device's offset. Every tick after that is measured through it.
 *
 * Capturing the offset once per payload is the whole point: recomputing it on
 * each render would subtract the same drift it just added and pin the clock to
 * the instant the payload was built, so it would never count down at all.
 *
 * Network latency is not corrected for. It shortens the clock by the one-way
 * trip, which is well inside the grace window the server already allows on a
 * vote that lands late.
 *
 * Returns null when there is no clock — every poll that predates this story.
 */
export function useCountdown(
  deadlineAt: string | null | undefined,
  serverTime: string | null | undefined,
): Countdown | null {
  // Re-renders on a timer. The value is unused: the arithmetic below reads the
  // clock directly rather than accumulating ticks, which would drift and would
  // stop entirely in a backgrounded tab.
  const [, setTick] = useState(0);

  const offsetRef = useRef(0);
  const seenServerTimeRef = useRef<string | null>(null);

  // Derived during render rather than in an effect: an effect would leave the
  // first paint after a screen change measuring against the previous screen's
  // offset. Idempotent for a given serverTime, so re-running it is harmless.
  if (serverTime && serverTime !== seenServerTimeRef.current) {
    seenServerTimeRef.current = serverTime;
    const parsed = Date.parse(serverTime);
    offsetRef.current = Number.isNaN(parsed) ? 0 : parsed - Date.now();
  }

  const running = Boolean(deadlineAt);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setTick((n) => n + 1), TICK_MS);
    return () => clearInterval(id);
  }, [running, deadlineAt]);

  if (!deadlineAt) return null;

  const remaining = msRemaining(deadlineAt, new Date(Date.now() + offsetRef.current));
  if (remaining === null) return null;

  // Ceil, so the clock reads 1 for the whole of the final second and hits 0
  // exactly when it expires — a floor would print 0 over a second still live.
  const secondsLeft = Math.ceil(remaining / 1000);

  return { secondsLeft, expired: remaining === 0, label: formatCountdown(secondsLeft) };
}

/** m:ss once past a minute, bare seconds below it. */
export function formatCountdown(secondsLeft: number): string {
  if (secondsLeft < 60) return String(secondsLeft);
  const minutes = Math.floor(secondsLeft / 60);
  return `${minutes}:${String(secondsLeft % 60).padStart(2, '0')}`;
}
